import { useState, useEffect } from 'react';
import { useAuthContext } from './AuthContext';
import { registerPublisher, publishPackage } from './communityApi';
import { trackEvent } from './analytics';
import './App.css';

export default function PublisherPage() {
  const { user, profile, refreshProfile } = useAuthContext();
  const isPublisher = profile?.publisherTier && profile.publisherTier !== 'user';
  const [tab, setTab] = useState(isPublisher ? 'publish' : 'register');

  useEffect(() => {
    if (isPublisher) setTab('publish');
  }, [isPublisher]);

  if (!user) {
    return (
      <div style={{ maxWidth: 600, margin: '80px auto', padding: '0 24px', textAlign: 'center' }}>
        <h1 style={{ fontSize: '1.5rem', color: '#f8fafc', marginBottom: 12 }}>Publisher Portal</h1>
        <p style={{ color: '#94a3b8', lineHeight: 1.6 }}>Sign in to register as a publisher and submit packages to the Hub.</p>
      </div>
    );
  }

  return (
    <div style={{ maxWidth: 600, margin: '40px auto', padding: '0 24px' }}>
      <h1 style={{ fontSize: '1.5rem', color: '#f8fafc', marginBottom: 4 }}>Publisher Portal</h1>
      <p style={{ color: '#94a3b8', marginBottom: 24, fontSize: '0.9rem' }}>
        {isPublisher ? `Tier: ${profile.publisherTier}` : 'Register to start publishing to the Hub.'}
      </p>

      <div style={{ display: 'flex', gap: 6, marginBottom: 24 }}>
        <TabButton label="Register" active={tab === 'register'} onClick={() => setTab('register')} />
        <TabButton label="Publish" active={tab === 'publish'} onClick={() => setTab('publish')} disabled={!isPublisher} />
      </div>

      {tab === 'register' && <RegisterForm profile={profile} isPublisher={isPublisher} onRegistered={async () => { await refreshProfile(); setTab('publish'); }} />}
      {tab === 'publish' && isPublisher && <PublishForm />}
    </div>
  );
}

function TabButton({ label, active, onClick, disabled }) {
  return (
    <button onClick={onClick} disabled={disabled} style={{
      padding: '8px 20px', borderRadius: 20, border: 'none',
      background: active ? '#f8fafc' : 'transparent',
      color: active ? '#0f172a' : (disabled ? '#475569' : '#94a3b8'),
      cursor: disabled ? 'default' : 'pointer',
      fontSize: '0.85rem', fontWeight: 500, fontFamily: 'inherit',
      transition: 'all 0.15s',
    }}>
      {label}
    </button>
  );
}

function RegisterForm({ profile, isPublisher, onRegistered }) {
  const [displayName, setDisplayName] = useState(profile?.displayName || profile?.handle || '');
  const [homepage, setHomepage] = useState('');
  const [bio, setBio] = useState(profile?.bio || '');
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);

  if (isPublisher) {
    return (
      <Card>
        <p style={{ color: '#94a3b8', margin: 0 }}>
          You're registered as a <strong style={{ color: '#f8fafc' }}>{profile?.publisherTier}</strong> publisher. Use the Publish tab to submit content.
        </p>
      </Card>
    );
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setStatus(null);
    try {
      await registerPublisher({ displayName, homepage, bio });
      if (onRegistered) await onRegistered();
    } catch (err) {
      setStatus({ ok: false, message: err.message });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card>
      <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        <Field label="Display Name *">
          <input value={displayName} onChange={(e) => setDisplayName(e.target.value)}
            required minLength={2} placeholder="Your publisher name" style={inputStyle} />
        </Field>
        <Field label="Homepage (optional)">
          <input value={homepage} onChange={(e) => setHomepage(e.target.value)}
            placeholder="https://yoursite.com" style={inputStyle} />
        </Field>
        <Field label="Bio (optional)">
          <textarea value={bio} onChange={(e) => setBio(e.target.value)}
            placeholder="Tell us about yourself..." rows={3} style={{ ...inputStyle, resize: 'vertical' }} />
        </Field>
        {status && <StatusMessage status={status} />}
        <button type="submit" disabled={loading} style={primaryBtnStyle}>
          {loading ? 'Registering...' : 'Register as Publisher'}
        </button>
      </form>
    </Card>
  );
}

function PublishForm() {
  const [name, setName] = useState('');
  const [summary, setSummary] = useState('');
  const [description, setDescription] = useState('');
  const [type, setType] = useState('skill');
  const [category, setCategory] = useState('utility');
  const [tags, setTags] = useState('');
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setStatus(null);
    try {
      const result = await publishPackage({
        name, summary, description, type,
        contentCategory: category,
        tags: tags.split(',').map((t) => t.trim()).filter(Boolean),
      });
      setStatus({ ok: true, message: `Submitted for review! Slug: ${result.slug}` });
      trackEvent('hub_publish', { artifact_type: type, is_update: false });
      setName(''); setSummary(''); setDescription(''); setTags('');
    } catch (err) {
      setStatus({ ok: false, message: err.message });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card>
      <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        <Field label="Package Name *">
          <input value={name} onChange={(e) => setName(e.target.value)}
            required minLength={2} placeholder="My Awesome Skill" style={inputStyle} />
        </Field>
        <Field label="Summary *">
          <input value={summary} onChange={(e) => setSummary(e.target.value)}
            required placeholder="One-line description" style={inputStyle} />
        </Field>
        <Field label="Description">
          <textarea value={description} onChange={(e) => setDescription(e.target.value)}
            placeholder="Detailed description..." rows={4} style={{ ...inputStyle, resize: 'vertical' }} />
        </Field>
        <div style={{ display: 'flex', gap: 12 }}>
          <Field label="Type" style={{ flex: 1 }}>
            <select value={type} onChange={(e) => setType(e.target.value)} style={inputStyle}>
              <option value="skill">Skill</option>
              <option value="pipeline">Pipeline</option>
              <option value="agent">Agent</option>
              <option value="miniApp">Mini App</option>
              <option value="theme">Theme</option>
            </select>
          </Field>
          <Field label="Category" style={{ flex: 1 }}>
            <select value={category} onChange={(e) => setCategory(e.target.value)} style={inputStyle}>
              <option value="utility">Utility</option>
              <option value="productivity">Productivity</option>
              <option value="education">Education</option>
              <option value="entertainment">Entertainment</option>
              <option value="developer">Developer</option>
              <option value="miniApp">Mini App</option>
            </select>
          </Field>
        </div>
        <Field label="Tags (comma-separated)">
          <input value={tags} onChange={(e) => setTags(e.target.value)}
            placeholder="ai, automation, productivity" style={inputStyle} />
        </Field>
        {status && <StatusMessage status={status} />}
        <button type="submit" disabled={loading} style={primaryBtnStyle}>
          {loading ? 'Submitting...' : 'Submit for Review'}
        </button>
      </form>
    </Card>
  );
}

// ── Shared Components ───────────────────────────────────────────────────────

function Card({ children }) {
  return (
    <div style={{
      background: '#1e293b', borderRadius: 14, padding: 24,
      border: '1px solid rgba(255,255,255,0.08)',
    }}>
      {children}
    </div>
  );
}

function Field({ label, children, style }) {
  return (
    <label style={{ display: 'flex', flexDirection: 'column', gap: 6, ...style }}>
      <span style={{ fontSize: '0.8rem', color: '#f8fafc', fontWeight: 500 }}>{label}</span>
      {children}
    </label>
  );
}

function StatusMessage({ status }) {
  return (
    <div style={{
      padding: '10px 14px', borderRadius: 8, fontSize: '0.85rem',
      background: status.ok ? 'rgba(52,199,89,0.1)' : 'rgba(255,59,48,0.1)',
      color: status.ok ? '#4ade80' : '#f87171',
      border: `1px solid ${status.ok ? 'rgba(52,199,89,0.2)' : 'rgba(255,59,48,0.2)'}`,
    }}>
      {status.message}
    </div>
  );
}

// ── Styles ──────────────────────────────────────────────────────────────────

const inputStyle = {
  padding: '10px 14px', borderRadius: 10,
  border: '1px solid rgba(255,255,255,0.08)',
  background: '#0f172a', color: '#f8fafc', fontSize: '0.85rem',
  fontFamily: 'inherit', outline: 'none',
  transition: 'border-color 0.15s',
};

const primaryBtnStyle = {
  padding: '12px 24px', borderRadius: 20, border: 'none',
  background: '#f8fafc', color: '#0f172a', fontSize: '0.9rem',
  cursor: 'pointer', fontWeight: 600, fontFamily: 'inherit', marginTop: 4,
  transition: 'opacity 0.15s',
};
